import { useState } from 'react';
import {
  Button,
  ButtonGroup,
  HStack,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import type { Editor } from '@tiptap/react';
import { Bold, Heading1Icon, Heading2Icon, Link2, Link2Off, List } from 'lucide-react';
import { FormatButton } from '@duke-hq/ui/components/DukeTiptapEditor/Menus/BubbleMenu/FormatButton';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';

/**
 * Five controls because the stored schema allows five things: bold, two heading
 * levels, bullet lists and links. Anything more would lose its formatting on save.
 */

function LinkControl({ editor }: { editor: Editor }): React.ReactElement {
  const [href, setHref] = useState('');
  const [open, setOpen] = useState(false);
  const active = editor.isActive('link');

  const apply = (): void => {
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    setHref('');
    setOpen(false);
  };

  return (
    <Popover
      isOpen={open}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      initialFocusRef={undefined}
    >
      <PopoverTrigger>
        <FormatButton
          label={t`link`}
          icon={Link2}
          isActive={active}
          onClick={() => {
            setHref(editor.getAttributes('link').href ?? '');
            setOpen(!open);
          }}
        />
      </PopoverTrigger>
      <PopoverContent width={pxToRem(320)}>
        <PopoverArrow />
        <PopoverBody>
          <Stack gap={2}>
            <Input
              size="sm"
              value={href}
              placeholder="https://"
              onChange={(e) => setHref(e.target.value)}
              data-testid={testIds.linkInput}
            />
            <ButtonGroup size="sm">
              <Button onClick={apply} isDisabled={href.trim() === ''}>
                <Trans>Apply</Trans>
              </Button>
              {active ? (
                <Button
                  variant="ghost"
                  leftIcon={<Link2Off size={14} />}
                  onClick={() => {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    setOpen(false);
                  }}
                >
                  <Trans>Remove</Trans>
                </Button>
              ) : null}
            </ButtonGroup>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

export function EmailFormatToolbar({ editor }: { editor: Editor | null }): React.ReactElement {
  if (editor === null) {
    return <HStack gap={0.5} data-testid={testIds.formatToolbar} />;
  }

  return (
    <HStack gap={0.5} data-testid={testIds.formatToolbar}>
      <FormatButton
        label={t`bold`}
        icon={Bold}
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <FormatButton
        label={t`heading 1`}
        icon={Heading1Icon}
        isActive={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      />
      <FormatButton
        label={t`heading 2`}
        icon={Heading2Icon}
        isActive={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      />
      <FormatButton
        label={t`bullet list`}
        icon={List}
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <LinkControl editor={editor} />
    </HStack>
  );
}
